/**
 * useNotifications — F 490's notification feed.
 *
 * WHY IT POLLS INSTEAD OF LISTENING
 * ---------------------------------
 * The notification is written by the SCHEDULER, which runs on the server,
 * on cron, in a different process from the browser. There is no socket
 * between them and no event to subscribe to — the browser cannot be told
 * that a node appeared in the graph while it was not looking.
 *
 * So the feed is a POLL. The interval is 15 seconds: fast enough that a
 * reminder feels immediate, slow enough that an open tab is not hammering
 * the graph.
 *
 * WHY THE POLL IS PAUSED WHEN THE TAB IS HIDDEN
 * ----------------------------------------------
 * A background tab is not looking at a toast. Polling it anyway spends the
 * operator's battery and the server's time on something nobody will see.
 * `visibilitychange` pauses the poll and RESUMES it immediately on return,
 * so the first thing the operator sees when they come back is a current
 * bell count rather than a stale one.
 *
 * WHY THE UNREAD COUNT IS DERIVED, NOT STORED
 * --------------------------------------------
 * The count is `notifications.filter(n => !n.read).length`, recomputed from
 * the list. There is no separate counter that could drift out of step with
 * the notifications it counts — the same reason a database never keeps a
 * "number of unread rows" column that is incremented by hand.
 *
 * THE TOAST IS DERIVED FROM THE FEED, TOO
 * ----------------------------------------
 * A toast is shown for a notification that has just been seen for the
 * FIRST time. "Just been seen" is tracked by a set of ids this hook has
 * already announced, and that set is in memory only: on a reload, an old
 * unread notification is NOT re-toasted, because a toast for something that
 * happened an hour ago is noise, not news.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Notification } from '../../types/models/Reminder';
import type { ReminderStore } from '../../core/reminder/store';

/** How often the feed refreshes while the tab is visible. */
export const NOTIFICATION_POLL_MS = 15_000;

export interface UseNotificationsResult {
  /** Every notification, newest first. */
  notifications: Notification[];
  /** The unread ones — what the bell counts. */
  unread: Notification[];
  /** The unread COUNT, for the badge. */
  unreadCount: number;
  /** The most recent notification that has not been announced yet. */
  pendingToast: Notification | null;
  /** Mark one notification read. */
  markRead: (id: string) => Promise<void>;
  /** Mark every notification read. */
  markAllRead: () => Promise<void>;
  /** Refresh now. */
  refresh: () => Promise<void>;
  /** The store in use, for callers that need the raw list. */
  store: ReminderStore;
}

export function useNotifications(store: ReminderStore): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  /** Ids already announced as a toast. In memory only — never persisted. */
  const announced = useRef<Set<string>>(new Set());
  /** True once the first load has happened, so the first poll does not toast. */
  const primed = useRef(false);
  const [pendingToast, setPendingToast] = useState<Notification | null>(null);

  const refresh = useCallback(async () => {
    let list: Notification[] = [];
    try {
      list = await store.listNotifications();
    } catch {
      // A failed read must not take the header down with it. The next poll
      // will try again.
      return;
    }
    setNotifications(list);

    if (!primed.current) {
      // The FIRST read is the baseline, not news. Nothing is toasted here.
      primed.current = true;
      for (const n of list) announced.current.add(n.id);
      return;
    }
    // A notification that is new to this session, and still unread, is
    // announced. `announced` holds every id ever seen, so a notification
    // that is read and then re-listed is not announced twice.
    const fresh = list.find((n) => n.read === false && !announced.current.has(n.id));
    for (const n of list) announced.current.add(n.id);
    if (fresh) setPendingToast(fresh);
  }, [store]);

  const markRead = useCallback(async (id: string) => {
    await store.markRead(id);
    await refresh();
  }, [store, refresh]);

  const markAllRead = useCallback(async () => {
    const all = await store.listNotifications();
    for (const n of all) {
      if (!n.read) await store.markRead(n.id);
    }
    await refresh();
  }, [store, refresh]);

  // ── the poll ────────────────────────────────────────────────────────
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') await refresh();
      if (!cancelled) timer = setTimeout(tick, NOTIFICATION_POLL_MS);
    };

    void tick();

    const onVisibility = () => {
      // On the way BACK to visible, refresh at once. The bell must not sit
      // there showing a count from before the tab was hidden.
      if (document.visibilityState === 'visible') {
        if (timer) { clearTimeout(timer); timer = null; }
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  const unread = useMemo(() => notifications.filter((n) => n.read === false), [notifications]);

  return {
    notifications,
    unread,
    unreadCount: unread.length,
    pendingToast,
    markRead,
    markAllRead,
    refresh,
    store,
  };
}
